import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  Modal,
  TextField,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
} from '@sunrun/experience-ui-components';
import { getTodayAsYYYYMMDD, validateDateRange } from 'helpers/dateHelpers';
import type { LeaderboardDateRange } from 'hooks/usePerformanceData';

type LeaderboardDateRangeModalProps = {
  initialRange: LeaderboardDateRange | null;
  onCancel: () => void;
  onConfirm: (range: LeaderboardDateRange) => void;
};

const LeaderboardDateRangeModal = ({
  initialRange,
  onCancel,
  onConfirm,
}: LeaderboardDateRangeModalProps) => {
  const today = useMemo(() => getTodayAsYYYYMMDD(), []);
  const [startDate, setStartDate] = useState(initialRange?.startDate ?? '');
  const [endDate, setEndDate] = useState(initialRange?.endDate ?? '');
  const isMobile = useSingletonMobileMediaQuery();
  const error = validateDateRange(startDate, endDate);
  const isConfirmDisabled = !startDate || !endDate || !!error;

  return (
    <div onMouseDown={(event) => event.stopPropagation()}>
      <Modal
        testId="leaderboard-date-range-modal"
        customModalWidth={640}
        type="info"
        hideIcon
        title="Leaderboard date filter"
        onClose={onCancel}
        secondaryButton={{
          text: 'Cancel',
          onClick: onCancel,
          testId: 'leaderboard-date-range-modal-cancel',
        }}
        primaryButton={{
          text: 'Confirm',
          onClick: () => onConfirm({ startDate, endDate }),
          disabled: isConfirmDisabled,
          testId: 'leaderboard-date-range-modal-confirm',
        }}>
        <DateRangeRow isMobile={isMobile}>
          <DateInput
            type="date"
            label="From"
            value={startDate}
            max={today}
            isError={!!error}
            onChange={(event) => setStartDate(event.target.value)}
          />
          {!isMobile && <RangeSeparator>-</RangeSeparator>}
          <DateInput
            type="date"
            label="To"
            value={endDate}
            max={today}
            isError={!!error}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </DateRangeRow>
        {error && <ErrorMessage size="small">{error}</ErrorMessage>}
      </Modal>
    </div>
  );
};

const DateRangeRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: ${({ isMobile }) => (isMobile ? 'stretch' : 'flex-end')};
  gap: 15px;
  width: 100%;
`;

const DateInput = styled(TextField)`
  flex: 1;
`;

const RangeSeparator = styled.span`
  padding-bottom: 16px;
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 16px;
  line-height: 1.4;
`;

const ErrorMessage = styled(Typography)`
  margin-top: 8px;
  color: ${tokens.ERROR_60};
`;

export { LeaderboardDateRangeModal };
